import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/ru';

import { authUserState } from './common';
import { checkRights, formatDate } from '../functions';
import { DateUtils, VATTitles } from '../dictionary';

/** Статусы списков покупок */
const ShopListStatuses = [
	{ Id: 1, Name: "Новый" },
	{ Id: 2, Name: "Отправлен" },
	{ Id: 3, Name: "Доставлен" }
];

export class ShopListState {
	constructor() {
		this.Lists = [];
		this.IsSendRequest = false;

		this.ShopList = undefined;
	}

	/** Право пользователя работать со списками покупок */
	get CanManage() { return authUserState.User ? checkRights("ManagePurchase") : false; }

	/** Формирование столбцов таблицы списков покупок */
	get columns() {
		return {
			Date: {
				filterable: false,
				Header: "Дата создания",
				width: 100,
				Cell: row => formatDate(row.original.Date)
			},
			User: {},
			Stock: {},
			Provider: {},
			Amount: {width: 100},
			Status: {
				filterable: false,
				accessor: "StatusName"
			},
			...(this.CanManage ? {
				View: {},
				Edit: {},
				Send: {},
				DownLoad: {},
				Remove: {}
			} : {})
		};
	}

	/** Разбор списков закупок от сервера
	 * @param {object} data ответ от сервера
	 */
	setLists(data) {
		this.Lists = data.Success ? _.orderBy(_.map(data.ShopLists, function (v) {
			return {
				...v,
				StatusName: (_.find(ShopListStatuses, { Id: v.Status }) || {}).Name || "",
				CanSend: v.Status === 1,
				CanEdit: v.Status === 1,
				CanView: v.Status !== 1,
				CanRemove: v.Status === 1
			};
		}), ['Status', 'Date'], ['asc', 'desc']) : [];
		this.IsSendRequest = true;
	}

	setList(shopList) {
		this.ShopList = !_.isUndefined(shopList) ? new ShopList(shopList, this.CanManage) : undefined;
	}
}
decorate(ShopListState, {
	ShopList: observable,
	Lists: observable,
	IsSendRequest: observable,

	CanManage: computed,
	columns: computed
});

/** Модель открытого списка покупок */
export class ShopList {
	constructor(item = {}, canManage = false) {
		this.Id = item.Id || "";
		this.StockId = item.StockId || -1;
		this.Stock = item.Stock || "";
		this.ProviderId = item.ProviderId || -1;
		this.ProviderName = item.Provider || "";
		this.UserId = item.UserId || -1;
		this.User = item.User || "";
		this.Date = item.Date || "";
		this.Status = item.Status || 1;

		this.MinAmount = !_.isUndefined(item.MinAmount) && !_.isNull(item.MinAmount) ? item.MinAmount : 0;
		this.Amount = !_.isUndefined(item.Amount) && !_.isNull(item.Amount) ? item.Amount : 0;

		this.Contacts = item.Contacts || [];
		this.WorkHours = undefined;

		this.Products = _.map(item.Products || [], (product) => new ShopListProduct(product));

		this.CanManageRight = canManage;

		this.ButtonLoading = false;
		this.SuccessSave = false;
		this.ErrorCode = "";
	}

	/** Возможность редактировать новый список покупок */
	get CanManage() { return this.CanManageRight && this.Status === 1; }

	/** Возможность удалить товарную строку */
	get CanRemove () { return this.CanManage && this.Products.length > 1}

	/** Название текущего статуса списка покупок */
	get StatusName() { return (_.find(ShopListStatuses, { Id: this.Status }) || {}).Name || ""; }

	/** Сумма заказа меньше минимальной суммы поставщика */
	get IsNotEnough() { return this.TotalAmount < this.MinAmount; }

	/** Общая сумма выбранных товаров */
	get TotalAmount() {
		return _.reduce(this.Products, (sum, product) => {
			const price = !_.isUndefined(product.Price) && !_.isNull(product.Price) ? product.Price : 0,
				count = !_.isUndefined(product.Count) && !_.isNull(product.Count) ? product.Count : 0;

			return sum + (price * count);
		}, 0);
	}


	/** Добавление пустой товарной строки */
	addProduct = () => { this.Products = _.concat(this.Products, new ShopListProduct()); }

	/** Удаление товарной строки по индексу */
	removeProduct = (index) => {
		this.Products = _.filter(this.Products, (product, i) => i !== index);
	}

	/** Сохранение данных поставщика и дополнение товарных строк его настройками */
	setProvider(provider) {
		this.MinAmount = !_.isUndefined(provider) && !_.isNull(provider.MinAmount) ? provider.MinAmount : this.MinAmount;
		this.WorkHours = !_.isUndefined(provider) && !_.isNull(provider.WorkHours) && !_.isEmpty(provider.WorkHours) ? new WorkDays(provider.WorkHours) : this.WorkHours;
		this.ProductList = !_.isUndefined(provider) ? _.map(provider.Products, (item) => {
			return {
				Id : item.ProductId,
				Name : item.Product,
				UnitNet : item.UnitNet,
				Price : item.Price,
				MinCount : item.MinCount,
				VAT : item.VAT,
			}
		}) : this.ProductList;

		if (!_.isUndefined(provider)) {
			_.each(this.Products, (item) => {
				item.updateProduct(_.find(this.ProductList, { Id: item.ProductId }));
			});
		}
	}

	/**
	 * Валидация формы перед отправкой запроса
	 * 1. Должны присутствовать продукты
	 * @returns {boolean}
	 */
	validate() {
		this.ErrorCode = "";

		var products = _.compact(_.map(this.Products, (product) => product.getData()));
		if (_.isEmpty(products)) {
			this.ErrorCode = "COUNT_PRODUCT_REQUERIED"
			return false;
		}

		return true;
	}

	/** Сбор полного payload списка покупок
	 * @returns {object} плоский объект вида
	 * { Id, "Products.0.ProductId", "Products.0.Count" }
	 */
	getData() {
		var data = { Id: this.Id },
			products = _.compact(_.map(this.Products, (product) => product.getData()));

		_.each(products, (product, i) => {
			var name = "Products." + i + ".";
			_.each(product, (value, id) => { data[name + id] = value; });
		});
		return data;
	}
}

decorate(ShopList, {
	Products: observable,
	WorkHours: observable,
	ProductList: observable,

	ButtonLoading: observable,
	SuccessSave: observable,
	ErrorCode: observable,

	CanManage: computed,
	CanRemove: computed,
	StatusName: computed,
	IsNotEnough: computed,
	TotalAmount: computed
});

/** Модель товарной строки списка покупок */
export class ShopListProduct {
	constructor(item = {}) {
		this.ProductId = item.ProductId || -1;
		this.Count = !_.isUndefined(item.Count) && !_.isNull(item.Count) ? item.Count : "";
		this.UnitNet = item.UnitNet || "";
		this.Price = !_.isUndefined(item.Price) && !_.isNull(item.Price) ? item.Price : "";
		this.MinCount = !_.isUndefined(item.MinCount) && !_.isNull(item.MinCount) ? item.MinCount : "";
		this.VAT = !_.isUndefined(item.VAT) && !_.isNull(item.VAT) ? item.VAT : -1;
	}

	/** Количество товара меньше заданного поставщиком минимального количества */
	get IsNotEnough() {
		return this.MinCount !== "" && !_.isUndefined(this.MinCount) && !_.isNull(this.MinCount)
			&& this.Count < this.MinCount;
	}

	/** Возвращает название ставки НДС */
	get VATTitle() {
		const vat = _.find(VATTitles, { Id: this.VAT });
		return vat ? vat.Name : "";
	}

	/**
	 * Сбор значения товарной строки и обновление настроек выбранного продукта
	 * @param {string} id название поля
	 * @param {*} value значение поля
	 * @param {object} product выбранный продукт поставщика
	 */
	collect(id, value, product) {
		this[id] = value;
		if (id === "ProductId") this.updateProduct(product);
	}

	/** Дополнение строки настройками товара поставщика */
	updateProduct(product) {
		this.UnitNet = product ? product.UnitNet : "";
		this.Price = product ? product.Price : "";
		this.MinCount = product ? product.MinCount : "";
		this.VAT = product ? product.VAT : -1;
	}

	/** Явные данные товарной строки для сохранения
	 * @returns {{ ProductId: number, Count: number }} объект с данными для сохранения
	*/
	getData() {
		if (_.isUndefined(this.ProductId) || _.isNull(this.ProductId) || this.ProductId === -1) return null;
		if (_.isUndefined(this.Count) || _.isNull(this.Count) || this.Count === "" || Number(this.Count) === 0) return null;
		return {
			ProductId: this.ProductId,
			Count: this.Count
		};
	}
}

decorate(ShopListProduct, {
	ProductId: observable,
	Count: observable,
	UnitNet: observable,
	Price: observable,
	MinCount: observable,
	VAT: observable,

	IsNotEnough: computed,
	VATTitle: computed
});

// TODO продумать read-only режим для общего WorkDays и заменить локальные WorkDays и WorkDaysInfo.
export class WorkDays {
	constructor(days =[]) {
		const week = DateUtils.getFullWeek(),
			today = this.Today;

		this.Days = _.sortBy(_.map(days, (day) => {
			const d = _.find(week, { Id: day.Day });

			return {
				Day: day.Day,
				Name: d ? d.Name : "",
				IsActive: day.Day === today.Day,
				StartTime: day.StartTime || "",
				EndTime: day.EndTime || ""
			};
		}), "Day");
	}

	/** Текущий день недели и дата для отображения графика */
	get Today() {
		const today = moment();

		return {
			Day: today.isoWeekday() - 1,
			Date: today.locale("ru").format("dd, DD.MM")
		};
	}
}
